import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, combineLatest, from, map, Observable, of, tap } from "rxjs";
import { SessionCategoryApiService } from "../../services/api/session-category-api.service";
import { SessionCategory, SessionCategoryBase } from "../../models/sessions/session-category.model";
import { StoreService } from "..";

@Injectable({
  providedIn: 'root'
})
export class SessionCategoryService {

  constructor(
    private store: StoreService,
    private sessionCategoryApiService: SessionCategoryApiService
  ) { }


  get sessionCategories$(): Observable<SessionCategory[]> {
    return this.store.state$.pipe(map(state => Object.values(state.sessionCategories)));
  }

  load(): Observable<SessionCategory[]> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return this.sessionCategoryApiService.getAll().pipe(
      tap((cats) => {
        const state = { ...this.store.state };
        cats.forEach((t) => state.sessionCategories[t.id] = t);
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading session categories:', error);
        throw error;
      })
    );
  }

  addSessionCategory(cat: SessionCategoryBase): Observable<SessionCategory> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.sessionCategoryApiService.add(cat)).pipe(
      tap((newCat) => {
        this.updateState(newCat);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error adding session category:', error);
        throw error;
      })
    );
  }

  updateSessionCategory(cat: SessionCategory): Observable<SessionCategory> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.sessionCategoryApiService.update(cat)).pipe(
      tap((cat) => {
        this.updateState(cat);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error updating session category:', error);
        throw error;
      })
    );
  }

  private updateState(cat: SessionCategory) {
    const state = { ...this.store.state };
    state.sessionCategories[cat.id] = cat;
    state.loading = false;
    this.store.setState(state);
  }

  deleteSessionCategory(id: number): Observable<void> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.sessionCategoryApiService.delete(id)).pipe(
      tap(() => {
        const state = { ...this.store.state };
        delete state.sessionCategories[id];
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error deleting session category:', error);
        throw error;
      })
    );
  }

  clear(): void {
    const state = { ...this.store.state };
    state.sessionCategories = {};
    this.store.setState(state);
  }
}
